import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Plus, List, LayoutGrid } from "lucide-react";
import { WorkforceCardsLayout } from "@/components/rdo/WorkforceCardsLayout";
import { DialogAddWorkforce } from "@/components/rdo/DialogAddWorkforce";
import { WorkforceIndividualEditModal } from "@/components/rdo/WorkforceIndividualEditModal";
import { WorkforceTeamEditModal } from "@/components/rdo/WorkforceTeamEditModal";
import { useViewModePersistence } from "@/hooks/useViewModePersistence";
import { RDO } from "@/domain/rdo/schema";

type ViewModeType = 'cards' | 'table';

interface WorkforceModuleProps {
  value: RDO["workforce"];
  onChange: (value: RDO["workforce"]) => void;
  mode: "create" | "edit" | "view";
}

export function WorkforceModule({ value, onChange, mode }: WorkforceModuleProps) {
  const { getPreference, updatePreference } = useViewModePersistence();
  const [workforceViewMode, setWorkforceViewMode] = useState<ViewModeType>(() => 
    getPreference('workforce', 'cards')
  );
  
  // Modal states
  const [showWorkforceModal, setShowWorkforceModal] = useState(false);
  const [showIndividualEditModal, setShowIndividualEditModal] = useState(false);
  const [showTeamEditModal, setShowTeamEditModal] = useState(false);
  const [editingWorkforceIndex, setEditingWorkforceIndex] = useState<number | null>(null);
  const [editingWorkforceData, setEditingWorkforceData] = useState<any>(null);

  // Calculate total workforce count.
  // Os dados trafegam no formato legado (passthrough): equipes usam
  // `totalMembers` e indivíduos usam `quantity` — mesma lógica do
  // WorkforceCardsLayout. Mantém fallback para o shape unificado (qty/members).
  const totalCount = value ? value.reduce((sum, item: any) => {
    if (item.type === 'team') {
      return sum + (item.totalMembers ?? item.members?.length ?? 0);
    }
    return sum + (item.quantity ?? item.qty ?? 0);
  }, 0) : 0;

  // Handle view mode change
  const handleViewModeChange = () => {
    const newMode = workforceViewMode === 'cards' ? 'table' : 'cards';
    setWorkforceViewMode(newMode);
    updatePreference('workforce', newMode);
  };

  // Handle edit workforce
  const handleEditWorkforce = (index: number) => {
    if (!value || index >= value.length) {
      console.warn('Invalid workforce index:', index);
      return;
    }
    
    const workforceItem = value[index];
    setEditingWorkforceIndex(index);
    setEditingWorkforceData(workforceItem);
    
    // Determine which modal to open based on item structure
    // Team items have teamId, teamName, totalMembers, members
    if ('teamId' in workforceItem && 'teamName' in workforceItem && 'members' in workforceItem) {
      // Consolidated team item
      setShowTeamEditModal(true);
    } else {
      // Individual item (person or isolated profession)
      setShowIndividualEditModal(true);
    }
  };

  // Handle delete workforce
  const handleDeleteWorkforce = (index: number) => {
    if (!value) return;
    
    const updatedWorkforce = value.filter((_, i) => i !== index);
    onChange(updatedWorkforce);
  };

  // Handle add workforce
  const handleAddWorkforce = (workforceData: any) => {
    if (editingWorkforceIndex !== null) {
      // Editing existing item
      const updatedWorkforce = [...(value || [])];
      
      if (workforceData.type === "team") {
        // For consolidated teams, preserve original structure
        updatedWorkforce[editingWorkforceIndex] = {
          ...workforceData,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      } else {
        // For individual items
        updatedWorkforce[editingWorkforceIndex] = {
          ...workforceData,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      }
      
      onChange(updatedWorkforce);
      setEditingWorkforceIndex(null);
    } else {
      // Adding new item
      const newWorkforceItem = {
        ...workforceData,
        observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
      };

      onChange([...(value || []), newWorkforceItem]);
    }
  };

  // Handle save individual edit
  const handleSaveIndividualEdit = (updatedWorkforce: any) => {
    if (editingWorkforceIndex === null) return;
    
    const updatedWorkforceList = [...(value || [])];
    
    // If quantity is 0, remove the item
    if (updatedWorkforce.quantity === 0) {
      updatedWorkforceList.splice(editingWorkforceIndex, 1);
    } else {
      updatedWorkforceList[editingWorkforceIndex] = updatedWorkforce;
    }
    
    onChange(updatedWorkforceList);
    setEditingWorkforceIndex(null);
    setEditingWorkforceData(null);
  };

  // Handle save team edit
  const handleSaveTeamEdit = (updatedTeam: any) => {
    if (editingWorkforceIndex === null) return;
    
    const updatedWorkforceList = [...(value || [])];
    
    // If updatedTeam is null, remove the team
    if (updatedTeam === null || updatedTeam.totalMembers === 0) {
      updatedWorkforceList.splice(editingWorkforceIndex, 1);
    } else {
      updatedWorkforceList[editingWorkforceIndex] = updatedTeam;
    }
    
    onChange(updatedWorkforceList);
    setEditingWorkforceIndex(null);
    setEditingWorkforceData(null);
  };

  // Get already added team names for the add workforce dialog
  const alreadyAddedTeamNames = value?.reduce((teams: string[], item: any) => {
    // For consolidated teams (have teamName property)
    if (item.teamName && !teams.includes(item.teamName)) {
      teams.push(item.teamName);
    }
    // For individual items with team assignment
    if (item.team && item.team !== 'sem-equipe' && !teams.includes(item.team)) {
      teams.push(item.team);
    }
    return teams;
  }, []) || [];

  const isReadOnly = mode === "view";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-4 h-4" />
            Mão de Obra ({totalCount})
          </CardTitle>
          {!isReadOnly && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleViewModeChange}
              >
                {workforceViewMode === 'cards' ? <List className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
              </Button>
              <Button onClick={() => setShowWorkforceModal(true)} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {value && value.length > 0 ? (
          <WorkforceCardsLayout 
            workforce={value as any} 
            showTotals={true} 
            viewMode={workforceViewMode}
            onEdit={!isReadOnly ? handleEditWorkforce : undefined}
            onDelete={!isReadOnly ? handleDeleteWorkforce : undefined}
          />
        ) : (
          <div className="text-center text-muted-foreground py-8">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma mão de obra adicionada</p>
          </div>
        )}
      </CardContent>

      {/* Modals */}
      <DialogAddWorkforce
        open={showWorkforceModal}
        onOpenChange={(open) => {
          setShowWorkforceModal(open);
          if (!open) {
            setEditingWorkforceIndex(null);
          }
        }}
        onAdd={handleAddWorkforce}
        editingData={editingWorkforceIndex !== null ? (
          editingWorkforceIndex === -1 ? 
            { isTeamEdit: true } : 
            value?.[editingWorkforceIndex]
        ) : undefined}
        alreadyAddedTeamNames={alreadyAddedTeamNames}
      />

      <WorkforceIndividualEditModal
        open={showIndividualEditModal}
        onOpenChange={(open) => {
          setShowIndividualEditModal(open);
          if (!open) {
            setEditingWorkforceIndex(null);
            setEditingWorkforceData(null);
          }
        }}
        workforce={editingWorkforceData || {}}
        onSave={handleSaveIndividualEdit}
      />

      <WorkforceTeamEditModal
        open={showTeamEditModal}
        onOpenChange={(open) => {
          setShowTeamEditModal(open);
          if (!open) {
            setEditingWorkforceIndex(null);
            setEditingWorkforceData(null);
          }
        }}
        teamData={editingWorkforceData || {}}
        onSave={handleSaveTeamEdit}
      />
    </Card>
  );
}